import * as React from 'react';
import { type AtomEffect, atom, useRecoilValue, useSetRecoilState } from 'recoil';

import { Alert, DialogRoot, DialogStep, DialogStepper, Video } from '@gitbook/spine-react';
import { localStorage } from '@gitbook/util-local-storage';

import { appReloadingMessageAtom } from '../../Application';
import {
    preloadSiteCentricWelcomeDialogInitialMedia,
    preloadSiteCentricWelcomeDialogMedia,
    releaseSiteCentricWelcomeDialogMedia,
    SITE_CENTRIC_WELCOME_DIALOG_POSTER_SOURCES,
    SITE_CENTRIC_WELCOME_DIALOG_STORAGE_KEY,
    SITE_CENTRIC_WELCOME_DIALOG_VIDEO_SOURCES,
} from './SiteCentricWelcomeDialogAssets';

const DIALOG_EXIT_ANIMATION_MEDIA_RELEASE_DELAY = 250;

const shouldShowSiteCentricWelcomeDialogAtom = atom<boolean>({
    key: 'shouldShowSiteCentricWelcomeDialogAtom',
    default: false,
    effects: [syncSiteCentricWelcomeDialogWithLocalStorage()],
});

function syncSiteCentricWelcomeDialogWithLocalStorage(): AtomEffect<boolean> {
    return ({ setSelf, onSet }) => {
        setSelf(localStorage.get<boolean>(SITE_CENTRIC_WELCOME_DIALOG_STORAGE_KEY) === true);

        onSet((newValue, _oldValue, isReset) => {
            if (isReset) {
                void localStorage.remove(SITE_CENTRIC_WELCOME_DIALOG_STORAGE_KEY);
                return;
            }

            void localStorage.set(SITE_CENTRIC_WELCOME_DIALOG_STORAGE_KEY, newValue);
        });
    };
}

/**
 * Welcome dialog shown once after a user opts in to the site-centric navigation.
 */
export function SiteCentricWelcomeDialog(): React.ReactElement | null {
    const appReloadingMessage = useRecoilValue(appReloadingMessageAtom);
    const shouldShowSiteCentricWelcomeDialog = useRecoilValue(
        shouldShowSiteCentricWelcomeDialogAtom
    );
    const [isInitialMediaReady, setIsInitialMediaReady] = React.useState(false);

    React.useEffect(() => {
        if (!shouldShowSiteCentricWelcomeDialog) {
            setIsInitialMediaReady(false);
            return;
        }

        let cancelled = false;

        void preloadSiteCentricWelcomeDialogInitialMedia().finally(() => {
            if (!cancelled) {
                setIsInitialMediaReady(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [shouldShowSiteCentricWelcomeDialog]);

    if (!shouldShowSiteCentricWelcomeDialog || !isInitialMediaReady) {
        return null;
    }

    return <SiteCentricWelcomeDialogContent isAppReloading={Boolean(appReloadingMessage)} />;
}

function SiteCentricWelcomeDialogContent(props: {
    isAppReloading: boolean;
}): React.ReactElement | null {
    const setShouldShowSiteCentricWelcomeDialog = useSetRecoilState(
        shouldShowSiteCentricWelcomeDialogAtom
    );
    const shouldReleaseMediaOnUnmountRef = React.useRef(false);

    React.useEffect(() => {
        return () => {
            if (!shouldReleaseMediaOnUnmountRef.current) {
                return;
            }

            window.setTimeout(() => {
                releaseSiteCentricWelcomeDialogMedia();
            }, DIALOG_EXIT_ANIMATION_MEDIA_RELEASE_DELAY);
        };
    }, []);

    React.useEffect(() => {
        void preloadSiteCentricWelcomeDialogMedia();
    }, []);

    if (props.isAppReloading) {
        return null;
    }

    return (
        <DialogRoot
            isOpen={true}
            onOpenChange={(open) => {
                if (!open) {
                    shouldReleaseMediaOnUnmountRef.current = true;
                    setShouldShowSiteCentricWelcomeDialog(false);
                }
            }}
        >
            <DialogStepper completeLabel="Try it now" showCancelAction={false}>
                {WELCOME_DIALOG_STEPS.map((step, index) => (
                    <DialogStep
                        key={step.title}
                        media={
                            <Video
                                src={SITE_CENTRIC_WELCOME_DIALOG_VIDEO_SOURCES[index]}
                                autoPlay
                                controls
                                aspectRatio={1272 / 720}
                                poster={SITE_CENTRIC_WELCOME_DIALOG_POSTER_SOURCES[index]}
                                className="rounded-md"
                            />
                        }
                        title={step.title}
                    >
                        {step.details}
                    </DialogStep>
                ))}
            </DialogStepper>
        </DialogRoot>
    );
}

const WELCOME_DIALOG_STEPS = [
    {
        title: 'A new way to navigate GitBook',
        details: (
            <p>
                Your sidebar now mirrors the site you're publishing. Open a site and you'll find its
                content, change requests, customization, insights, and settings in one place, so you
                stay focused on the site you're working on.
            </p>
        ),
    },
    {
        title: 'Find everything for a site in one place',
        details: (
            <>
                <p>
                    Open a site and your sidebar shows everything about it: content and structure on
                    top, with change requests, customization, insights, and settings below.
                </p>
                <p>
                    Spaces that aren't part of a site still have a home. They live in All Content at
                    the org level, where you can browse, jump in, or move them into a site whenever
                    you're ready.
                </p>
            </>
        ),
    },
    {
        title: 'Work in progress, safely inside your site',
        details: (
            <>
                <p>
                    Now that your content lives inside your site, GitBook gives you a way to work on
                    it without publishing it.
                </p>
                <p>
                    New sections start as drafts and can be previewed alongside your live content.
                    When a section is ready, switch off Draft on the section itself and it becomes
                    available to visitors immediately.
                </p>
            </>
        ),
    },
    {
        title: "We're listening",
        details: (
            <>
                <p>
                    This is a meaningful change to how GitBook works, and we want to hear how it
                    lands for you. Look for the feedback button on your org home and every site
                    overview to tell us what's working and what isn't.
                </p>
                <Alert
                    kind="default"
                    size="small"
                    title="Manage this setting from your organization menu."
                    className="mt-4"
                />
            </>
        ),
    },
] satisfies Array<{
    title: string;
    details: React.ReactNode;
}>;
